import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  List,
  ListItem,
  Typography,
  IconButton,
  Pagination,
  TextField,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Select,
  ListItemText,
  Collapse,
  useMediaQuery,
  Stack,
  Chip,
  Tooltip,
  keyframes,
} from "@mui/material";
import {
  Filter,
  Scale,
  FileText,
  Bookmark,
  BookmarkPlus,
  Search,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
  StickyNote,
  Trash2,
  Mic,
  Square,
  Info,
  Hash,
  Link2,
} from "lucide-react";

import {
  searchSections,
  statuteSearchResults,
} from "../actions/statuteSearchActions";
import InfoDialog from "../dialogs/InfoDialog";
import StatutePdfViewer from "../dialogs/StatutePdfViewerDialog";
import StatuteDetailDialog from "../dialogs/StatuteDetailDialog";
import { showInsufficientCreditsModal } from "../actions/creditActions";
import { deductCredits } from "../utils/HelperFunction/deductCredits";
import { fetchBookmarkGroups } from "../store/Bookmarks/bookmarksActions";
import { getHighlightedSentences } from "../utils/HelperFunction/getHighlightedSentences";
import SuggestionsDropdown from "./UI/SuggestionsDropdown";
import { debounce } from "lodash";
import CollapsibleList from "./UI/CollapsibleList";
import { toast } from "react-toastify";
import { CMSProvider } from "./CaseManagement/context/CaseManagmentContext";
import ReferenceCaseModal from "../dialogs/ReferenceCaseDialog";
import ToggleButton from "./UI/ToggleButton";
import AudioVisualizer from "./UI/AudioVisualizer";
import useAudioProcessor from "../utils/hooks/useAudioProcessor";
import BookmarkDialog from "../dialogs/BookmarkDialog";
import DateChip from "./UI/DateChip";
import { addStatuteReferenceApi } from "../services/api/caseManagementApi";
import { fetchStatuteSuggestionsApi } from "../services/api/statuteSearchApi";
import { removeStatuteFromCaseGroupApi } from "../services/api/bookmarksApi";
import { BACKEND_ENDPOINTS } from "../services/apiEndpoints";
import LogoLoader from "./UI/LogoLoader";

const pulse = keyframes` 0% {
    box-shadow: 0 0 0 0 rgba(255, 64, 129, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 64, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 64, 129, 0);
  }`;

const StatuteSearch = ({
  toggleSidebar,
  selectedLetter,
  selectedYear,
  searchMode,
  updateSearchMode,
  resultsCount,
  updateResultsCount,
  currentPage,
  updateCurrentPage,
  statuteSearchValue,
  updateStatuteSearchValue,
  selectedStatuteProvinceFilter,
  selectedStatuteTypeFilter,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const { state } = useLocation();
  const { statuteName } = useParams();
  const navigate = useNavigate();

  const [openBookmarkModal, setOpenBookmarkModal] = useState(false);
  const [selectedStatute, setSelectedStatute] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [bookmarkedStatuteIds, setBookmarkedStatuteIds] = useState(new Set());
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [placeholder, setPlaceholder] = useState("Search by title...");
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [isStatuteSearch, setIsStatuteSearch] = useState(false);
  const [openPdfPreview, setOpenPdfPreview] = useState(false);
  const [openSectionsDialog, setOpenSectionsDialog] = useState(false);
  const [isMount, setIsMount] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [openRelevantCases, setOpenRelevantCases] = useState({});
  const [openPdf, setOpenPdf] = useState({});
  const [referenceStatute, setReferenceStatute] = useState(null);
  // for adding reference case below are there states, which would be removed in future
  const [open, setOpen] = useState(false);
  const [selectedReferenceStatute, setSelectedReferenceStatute] = useState("");
  const [isAddStatuteLoading, setIsAddStatuteLoading] = useState(false);

  const audioData = useAudioProcessor(isListening);

  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedReferenceStatute("");
  };

  const handleAddStatuteToCaseManagement = async () => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    if (!selectedReferenceStatute) {
      console.error("No reference statute selected.");
      return;
    }

    setIsAddStatuteLoading(true);
    try {
      const url = `${BACKEND_ENDPOINTS.BASE_URL}${BACKEND_ENDPOINTS.CASE_MANAGEMENT_CASES}/${selectedReferenceStatute}/statute-references`;

      const response = await addStatuteReferenceApi(
        selectedReferenceStatute,
        referenceStatute?.Bookmark_ID,
      );
      const data = response.data;

      if (response.status !== 200) {
        toast.error(data.message || "Failed to add case reference.");
      }

      handleClose();
      // Deduct credits using the helper
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Add Statute to Case management system",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error adding case:", error);
    } finally {
      setIsAddStatuteLoading(false);
      handleClose();
    }
  };
  const isInitialSearchDone = useRef(false);
  const itemRefs = useRef([]);
  const latestQueryRef = useRef("");
  const paperRef = useRef(null);

  const dispatch = useDispatch();

  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const statuteSections = useSelector((state) => state.statuteSections);
  const { loading, sections } = statuteSections;

  const bookmarkGroups = useSelector((state) => state.bookmarkGroups);
  const { groups } = bookmarkGroups;

  const statuteSearch = useSelector((state) => state.statuteSearch);
  const { loading: statuteLoading, results } = statuteSearch;

  const isStatuteFilterApplied = useSelector(
    (state) => state.isStatuteFilterApplied,
  );

  const totalResults = results?.count || 0;

  const startRecognition = () => {
    setIsListening(true);
    shouldListenRef.current = true;
    recognitionRef.current?.start();
  };

  const stopRecognition = () => {
    recognitionRef.current?.stop();
    shouldListenRef.current = false;
    setIsListening(false);
  };

  const cancelVoiceInput = () => {
    stopRecognition();
    updateStatuteSearchValue("");
  };

  const acceptVoiceInput = () => {
    stopRecognition();
    if (!statuteSearchValue.trim()) return;
    handleSearch(statuteSearchValue);
  };

  const fetchSuggestions = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setSuggestions([]);
        setIsDropdownOpen(false);
        return;
      }
      latestQueryRef.current = query;
      setLoadingSuggestions(true);
      try {
        const response = await fetchStatuteSuggestionsApi(query);
        const responseData = response?.data;
        if (query === latestQueryRef.current) {
          setSuggestions(responseData?.data || []);
          setIsDropdownOpen(true);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 500),
    [],
  );

  const handleCloseInfoDialog = () => {
    setOpenInfoDialog(false);
  };

  const formatStatuteTitleHtml = (htmlString) => {
    if (!htmlString) return "";

    // Parse the HTML string into a DOM element
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Function to apply title case to each word
    const titleCaseText = (text) => {
      return text
        .toLowerCase()
        .split(" ")
        .map((word) => {
          // Preserve acronyms (fully uppercase words)
          if (word.length === 1 || word === word.toUpperCase()) {
            return word;
          }
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(" ");
    };

    // Function to traverse the DOM and apply title case to text nodes only
    const traverseAndTitleCase = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          // Only apply title case to the text content, not HTML tags
          child.textContent = titleCaseText(child.textContent);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          // Recurse through element nodes
          traverseAndTitleCase(child);
        }
      });
    };

    // Start traversal and title case application
    traverseAndTitleCase(doc.body);

    // Return the modified HTML string
    return doc.body.innerHTML;
  };

  const formatStatuteTitle = (title) => {
    if (!title) return;
    const titleCaseTitle = title
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return titleCaseTitle;
  };

  const formatStatuteSection = (title) => {
    if (!title) return;
    const titleCaseTitle = title
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return titleCaseTitle;
  };

  const handlePageChange = (event, value) => {
    updateCurrentPage(value);
    dispatch({
      type: "STATUTE_PAGINATION_SUCCESS",
      payload: { pageNumber: value, resultsCount },
    });
  };

  const handleSwitchChange = (value) => {
    if (value === "Title") {
      setPlaceholder("Search by title...");
      updateSearchMode("Title");
    } else {
      setPlaceholder("Search by content...");
      updateSearchMode("Section");
    }
  };

  const handleBookmarkClick = (statute, type) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    setSelectedStatute({ statute, type });
    const defaultGroup = groups?.find(
      (group) => group.groupName === "Bookmarks",
    );
    setSelectedGroup(defaultGroup?._id);
    setOpenBookmarkModal(true);
    const deductionSuccess = deductCredits(
      { userId, creditsToDeduct: 1, event: "Bookmark on Statute Page" },
      dispatch,
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification.",
      );
    }
  };

  const handleCloseBookmarkModal = () => {
    setOpenBookmarkModal(false);
    setSelectedGroup("");
  };

  const findGroupIdByStatuteId = (documents, statuteId) => {
    const group = documents.find((doc) =>
      doc.statutes.some((statuteItem) => statuteItem.id === statuteId),
    );

    return group ? group._id : null;
  };

  const handleUnBookmarkStatute = async (statuteId) => {
    try {
      let creditsToDeduct = 1;
      if (credits <= 0 || credits <= creditsToDeduct) {
        dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
        return;
      }
      const groupId = findGroupIdByStatuteId(groups, statuteId);
      const response = await removeStatuteFromCaseGroupApi(
        userInfo._id,
        groupId,
        statuteId,
      );

      if (response.status === 200) {
        setSnackbarMessage({
          type: "success",
          text: "Bookmark removed",
        });
        // Update the state by removing the statute ID from bookmarked statute IDs
        setBookmarkedStatuteIds((prevIds) => {
          const updatedIds = new Set(prevIds);
          updatedIds.delete(statuteId);
          return updatedIds;
        });
        const deductionSuccess = deductCredits(
          { userId, creditsToDeduct: 1, event: "UnBookmark on Statute Page" },
          dispatch,
        );

        if (!deductionSuccess) {
          console.warn(
            "Failed to deduct credits. Ensure retry or user notification.",
          );
        }
      } else {
        console.error("Error deleting bookmarked statute");
      }
    } catch (error) {
      console.error("Error deleting bookmarked statute:", error);
    }
  };

  const handleStatuteNameClick = (name) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    const cleanedName = name.replace(/<\/?[^>]+(>|$)/g, "");
    dispatch(searchSections("", cleanedName, searchMode));
    dispatch({ type: "SET_STATUTE_TITLE", payload: cleanedName });
    setOpenSectionsDialog(true);
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Sections button click on statute page",
      },
      dispatch,
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification.",
      );
    }
  };

  const copyStatuteLink = (name) => {
    const cleanedName = name.replace(/<\/?[^>]+(>|$)/g, "");
    const link = `${window.location.origin}/app/statute-search/${encodeURIComponent(cleanedName)}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const handleCloseSectionsDialog = () => {
    setOpenSectionsDialog(false);
    dispatch({ type: "RESET_STATUTE_TITLE" });
    if (statuteName) {
      navigate("/app/statute-search", { replace: true });
    }
  };

  const hasHandledStatuteDeepLink = useRef(false);

  useEffect(() => {
    if (!statuteName || hasHandledStatuteDeepLink.current) return;
    hasHandledStatuteDeepLink.current = true;
    // useParams() already URL-decodes the segment — decoding again would throw
    // on names containing a literal "%" (e.g. "50% Reduction Act").
    handleStatuteNameClick(statuteName);
  }, [statuteName]);

  const handleSectionsClick = (name, id) => {
    setOpenSections((prev) => {
      const isAlreadyOpen = prev[id];

      const newState = isAlreadyOpen ? {} : { [id]: true };

      if (!isAlreadyOpen) {
        const cleanedName = name.replace(/<\/?[^>]+(>|$)/g, "");
        dispatch(searchSections("", cleanedName, searchMode));
        setOpenRelevantCases({});
        setOpenPdf({});
      }

      return newState;
    });
  };

  const handleRelevantCasesClick = (id) => {
    setOpenRelevantCases((prev) => {
      const isAlreadyOpen = prev[id];

      const newState = isAlreadyOpen ? {} : { [id]: true };

      if (!isAlreadyOpen) {
        setOpenSections({});
        setOpenPdf({});
      }

      return newState;
    });
  };

  const handleSearch = (keyword) => {
    if (!keyword) return;
    setIsSearchClicked(true);
    const pageNumber = 1;
    updateCurrentPage(1);
    dispatch(
      statuteSearchResults(
        keyword,
        searchMode,
        resultsCount,
        (pageNumber - 1) * resultsCount,
        {
          year: selectedYear,
          letter: selectedLetter,
          province: selectedStatuteProvinceFilter,
          type: selectedStatuteTypeFilter,
        },
      ),
    );
  };

  const handlePdfClick = (url, id) => {
    setOpenPdf((prev) => {
      const isAlreadyOpen = prev[id];

      const newState = isAlreadyOpen ? {} : !url ? { [id]: true } : {};

      if (!isAlreadyOpen) {
        setOpenSections({});
        setOpenRelevantCases({});
      }

      return newState;
    });
    if (!url) return;
    window.open(url, "_blank");
  };

  const handleSuggestionClick = (suggestion) => {
    updateStatuteSearchValue(suggestion);
    setIsDropdownOpen(false);
    setSuggestions([]);
    handleSearch(suggestion);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    updateStatuteSearchValue(value);
    setFocusedIndex(-1);

    if (value.trim() === "") {
      fetchSuggestions.cancel();
      setIsDropdownOpen(false);
      setSuggestions([]);
      return;
    }
    setIsDropdownOpen(true);
    fetchSuggestions(value);
  };

  const handleKeyDown = (e) => {
    //if (!isDropdownOpen || suggestions?.length === 0) return;

    if (e.key === "ArrowDown") {
      if (suggestions.length === 0) return;
      setFocusedIndex((prevIndex) => (prevIndex + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      if (suggestions.length === 0) return;
      setFocusedIndex((prevIndex) =>
        prevIndex === -1
          ? suggestions.length - 1
          : (prevIndex - 1 + suggestions.length) % suggestions.length,
      );
    } else if (e.key === "Enter") {
      if (statuteSearchValue?.trim() === "") return;
      if (focusedIndex === -1) {
        handleSearch(statuteSearchValue);
      } else {
        handleSearch(suggestions[focusedIndex]?.queryPlusText);
        updateStatuteSearchValue(suggestions[focusedIndex]?.queryPlusText);
      }
      setIsDropdownOpen(false);
      setSuggestions([]);
      setFocusedIndex(-1);
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setIsDropdownOpen(false);
      setFocusedIndex(-1);
    }
  };

  useEffect(() => {
    if (state?.searchQuery) {
      updateStatuteSearchValue(state.searchQuery);
    }
  }, [state?.searchQuery]);

  useEffect(() => {
    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      const interimTranscript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join("");
      updateStatuteSearchValue(interimTranscript);
    };

    recognition.onerror = (event) => {
      console.error("Speech error:", event.error);
      stopRecognition();
    };

    recognition.onend = () => {
      if (shouldListenRef.current) {
        recognition.start();
      } else {
        // actually stopped by user
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;
  }, []);

  useEffect(() => {
    if (!isInitialSearchDone.current) {
      dispatch(
        statuteSearchResults(
          statuteSearchValue || state?.searchQuery || "",
          searchMode,
          resultsCount,
          (currentPage - 1) * resultsCount,
          {
            year: selectedYear,
            letter: selectedLetter,
            province: selectedStatuteProvinceFilter,
            type: selectedStatuteTypeFilter,
          },
        ),
      );
      isInitialSearchDone.current = true;
      state?.searchQuery && setIsSearchClicked(true);
      setIsMount(true);
    }
  }, [currentPage, statuteSearchValue, searchMode, resultsCount, dispatch]);

  useEffect(() => {
    if (isMount) {
      dispatch(
        statuteSearchResults(
          statuteSearchValue,
          searchMode,
          resultsCount,
          (currentPage - 1) * resultsCount,
          {
            year: selectedYear,
            letter: selectedLetter,
            province: selectedStatuteProvinceFilter,
            type: selectedStatuteTypeFilter,
          },
        ),
      );
    }
  }, [currentPage, resultsCount]);

  useEffect(() => {
    setOpenSections({});
  }, [isStatuteFilterApplied]);

  // Default search results on clearing search query
  useEffect(() => {
    if (!statuteSearchValue && isSearchClicked) {
      dispatch(
        statuteSearchResults(
          "",
          searchMode,
          resultsCount,
          (currentPage - 1) * resultsCount,
          {
            year: selectedYear,
            letter: selectedLetter,
            province: selectedStatuteProvinceFilter,
            type: selectedStatuteTypeFilter,
          },
        ),
      );
      setOpenSections({});
      setIsSearchClicked(false);
    }
  }, [
    dispatch,
    statuteSearchValue,
    isSearchClicked,
    searchMode,
    resultsCount,
    currentPage,
  ]);

  useEffect(() => {
    // Fetch bookmark groups when the component mounts or when userInfo changes
    if (userInfo._id) {
      dispatch(fetchBookmarkGroups(userInfo._id));
    }
    return () => updateStatuteSearchValue("");
  }, []);

  useEffect(() => {
    if (groups) {
      // Extract all statute IDs from the statutes array in each group document
      const allStatuteIds = groups?.flatMap((group) =>
        group.statutes.map((statute) => statute.id),
      );

      // Set the bookmark groups and statute IDs
      setBookmarkedStatuteIds(new Set(allStatuteIds)); // Store IDs in a Set for fast lookup
      const defaultGroup = groups?.find(
        (group) => group.groupName === "Bookmarks",
      );
      setSelectedGroup(defaultGroup?._id);
    }
  }, [groups]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (paperRef.current && !paperRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      fetchSuggestions.cancel();
    };
  }, [fetchSuggestions]);

  // Scroll to the focused item when focusedIndex changes
  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex].scrollIntoView({
        behavior: "auto",
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  return (
    <>
      <Helmet>
        <title>LawGPT - Statute Search</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          boxSizing: "border-box",
          flexDirection: "column",
          height: "calc(100vh - 50px)",
          width: { xs: "100%", lg: "80%" },
          flex: 5,
          p: { xs: "12px 12px 0 12px", lg: "16px 16px 0 16px" },
          backgroundColor: "#F8FAFC",
        }}
      >
        <Box
          sx={{
            position: "relative",
            backgroundColor: "#FFFFFF",
            boxSizing: "border-box",
            padding: "20px",
            borderRadius: "16px",
            border: "1px solid #F1F5F9",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
            marginBottom: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <IconButton
              size="small"
              sx={{
                display: { xs: "flex", lg: "none" },
                color: "#64748B",
                p: 1,
                borderRadius: "10px",
                backgroundColor: "#F8FAFC",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                  color: "#475569",
                },
              }}
              onClick={toggleSidebar}
            >
              <Filter size={20} />
            </IconButton>
            <Box sx={{ position: "relative", flex: 1 }}>
              <TextField
                variant="outlined"
                autoFocus
                placeholder={isListening ? "" : placeholder}
                value={isListening ? "" : statuteSearchValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                fullWidth
                sx={{
                  width: "100%",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    "& fieldset": {
                      borderColor: "#E2E8F0",
                      borderWidth: "1px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#CBD5E1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#00509e",
                      borderWidth: "2px",
                    },
                  },
                  "& input": {
                    padding: "14px 16px",
                    fontSize: "15px",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    color: "#1E293B",
                    "&::placeholder": {
                      color: "#94A3B8",
                      opacity: 1,
                    },
                  },
                }}
                InputProps={{
                  sx: { borderRadius: 12, p: 0 },
                  startAdornment: (
                    <Search
                      size={20}
                      color="#64748B"
                      style={{ marginLeft: "16px" }}
                    />
                  ),
                  endAdornment: loadingSuggestions ? (
                    <Box sx={{ display: "flex", marginRight: "16px" }}>
                      <LogoLoader
                        size={isMobileView ? "sm" : "sm"}
                        duration="1s"
                      />
                    </Box>
                  ) : isListening ? (
                    <>
                      <Tooltip title="Discard" placement="top">
                        <IconButton
                          size="small"
                          onClick={cancelVoiceInput}
                          sx={{
                            color: "#64748B",
                            marginRight: "8px",
                            backgroundColor: "#FEF2F2",
                            "&:hover": {
                              backgroundColor: "#FEE2E2",
                              color: "#DC2626",
                            },
                          }}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Stop" placement="top">
                        <IconButton
                          size="small"
                          sx={{
                            marginRight: "16px",
                            backgroundColor: "#DC2626",
                            color: "#fff",
                            borderRadius: "8px",
                            animation: `${pulse} 1.1s infinite`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "#B91C1C",
                            },
                          }}
                          onClick={acceptVoiceInput}
                        >
                          <Square size={16} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Speak" placement="top">
                      <IconButton
                        sx={{
                          color: "#64748B",
                          marginRight: "16px",
                          backgroundColor: "#F0F9FF",
                          "&:hover": {
                            backgroundColor: "#E0F2FE",
                            color: "#0284C7",
                          },
                        }}
                        onClick={startRecognition}
                      >
                        <Mic size={20} />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              {isListening && (
                <AudioVisualizer
                  isListening={isListening}
                  audioLevel={audioData}
                  component="statute"
                />
              )}
              {isDropdownOpen && suggestions.length > 0 && (
                <SuggestionsDropdown
                  suggestions={suggestions}
                  focusedIndex={focusedIndex}
                  onSuggestionClick={handleSuggestionClick}
                  itemRefs={itemRefs}
                  ref={paperRef}
                />
              )}
            </Box>
            {isMobileView ? (
              <FormControl
                variant="outlined"
                sx={{
                  minWidth: "120px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: "52px",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    backgroundColor: "#F8FAFC",
                  },
                  "& fieldset": {
                    borderColor: "#E2E8F0",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#CBD5E1",
                  },
                  "& .Mui-focused fieldset": {
                    borderColor: "#00509e",
                    borderWidth: "2px",
                  },
                  "& .MuiSelect-select": {
                    padding: "14px 16px",
                    fontSize: "14px",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    color: "#1E293B",
                  },
                }}
              >
                <Select
                  id="searchModeSelector"
                  value={searchMode}
                  onChange={(e) => {
                    e.target.value === "Title"
                      ? setPlaceholder("Search by title...")
                      : setPlaceholder("Search by content...");
                    updateSearchMode(e.target.value);
                  }}
                  sx={{
                    fontSize: "14px",
                    height: "52px",
                    bgcolor: "#F8FAFC",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  }}
                >
                  <MenuItem
                    value="Title"
                    sx={{
                      fontSize: "14px",
                      fontFamily:
                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    }}
                  >
                    {isMobileView ? "Title" : "Search by Title"}
                  </MenuItem>
                  <MenuItem
                    value="Section"
                    sx={{
                      fontSize: "14px",
                      fontFamily:
                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    }}
                  >
                    {isMobileView ? "Content" : "Search by Content"}
                  </MenuItem>
                </Select>
              </FormControl>
            ) : (
              <ToggleButton onChange={handleSwitchChange} component="statute" />
            )}
          </Box>
        </Box>
        {statuteLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 20px",
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              border: "1px solid #F1F5F9",
              marginBottom: "10px",
            }}
          >
            <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
          </Box>
        ) : results?.count === 0 && !loading ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "200px",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              border: "1px solid #F1F5F9",
              marginBottom: "16px",
            }}
          >
            <Box
              sx={{
                fontSize: { xs: "40px", lg: "60px" },
                marginBottom: "16px",
                color: "#CBD5E1",
              }}
            >
              <Search size={isMobileView ? 40 : 60} color="#CBD5E1" />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "18px", lg: "24px" },
                marginBottom: "8px",
                fontWeight: 600,
                color: "#475569",
                fontFamily:
                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              No Results Found
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "14px", lg: "16px" },
                color: "#64748B",
                textAlign: "center",
                fontFamily:
                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              Please review your search term or filter criteria.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 20px",
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                border: "1px solid #F1F5F9",
                marginBottom: "16px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "16px", lg: "18px" },
                    color: "#1E293B",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  }}
                >
                  <span style={{ color: "#00509e", fontWeight: "700" }}>
                    {totalResults.toLocaleString()}{" "}
                  </span>{" "}
                  {totalResults === 9981
                    ? "Total Statutes"
                    : searchMode === "Section" &&
                        totalResults > 0 &&
                        isSearchClicked
                      ? "Results Found"
                      : "Statutes Found"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#64748B",
                    fontWeight: "500",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  }}
                >
                  Show:
                </Typography>
                <FormControl
                  variant="outlined"
                  sx={{
                    width: isMobileView ? 90 : 100,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      height: "36px",
                      backgroundColor: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "& .Mui-focused fieldset": {
                      border: "none",
                    },
                    "& .MuiSelect-icon": {
                      right: "8px",
                      color: "#64748B",
                    },
                    "& .MuiSelect-select": {
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      fontFamily:
                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      color: "#1E293B",
                    },
                  }}
                >
                  <Select
                    id="resultsCount"
                    value={resultsCount}
                    onChange={(e) => updateResultsCount(e.target.value)}
                    sx={{
                      fontSize: "14px",
                      height: "36px",
                      bgcolor: "transparent",
                      fontFamily:
                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    }}
                  >
                    <MenuItem
                      value={10}
                      sx={{
                        fontSize: "14px",
                        fontFamily:
                          "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      10 results
                    </MenuItem>
                    <MenuItem
                      value={20}
                      sx={{
                        fontSize: "14px",
                        fontFamily:
                          "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      20 results
                    </MenuItem>
                    <MenuItem
                      value={50}
                      sx={{
                        fontSize: "14px",
                        fontFamily:
                          "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      }}
                    >
                      50 results
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              sx={{
                display: { xs: "flex", lg: "none" },
                justifyContent: "start",
                width: "100%",
                marginBottom: "16px",
              }}
            >
              {[
                { label: "Letter", value: selectedLetter },
                { label: "Year", value: selectedYear },
                { label: "Statute Type", value: selectedStatuteTypeFilter },
                { label: "Province", value: selectedStatuteProvinceFilter },
              ]
                .filter((filter) => filter.value)
                .map((filter, index) => (
                  <Chip
                    key={index}
                    size="small"
                    label={`${filter.label}: ${filter.value}`}
                    sx={{
                      backgroundColor: "#F0F9FF",
                      color: "#00509e",
                      fontSize: "12px",
                      fontWeight: "500",
                      borderRadius: "8px",
                      border: "1px solid #E0F2FE",
                      fontFamily:
                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      "& .MuiChip-label": {
                        padding: "4px 8px",
                      },
                    }}
                  />
                ))}
            </Stack>
            <List
              sx={{
                flexGrow: 1,
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                border: "1px solid #F1F5F9",
                overflowY: "auto",
                scrollBehavior: "smooth",
                "::-webkit-scrollbar": {
                  width: "6px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#F8FAFC",
                  borderRadius: "3px",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "3px",
                  "&:hover": {
                    background: "#94A3B8",
                  },
                },
              }}
            >
              {results &&
                results?.["search_results"]?.map((statute, index) => {
                  const isPdfDisabled = !statute?.Blob_Url;

                  return (
                    <ListItem
                      key={statute.id}
                      sx={{
                        alignItems: "flex-start",
                        padding: "0px",
                        borderBottom: "1px solid #F1F5F9",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          backgroundColor: "#FFFFFF",
                          borderRadius: "12px",
                          border: "1px solid #F1F5F9",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                            borderColor: "#E2E8F0",
                          },
                        }}
                      >
                        <Box sx={{ padding: "16px 20px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            {/* Title Section */}
                            <Typography
                              sx={{
                                fontWeight: "600",
                                fontSize: { xs: "14px", md: "16px" },
                                color: "#1E293B",
                                lineHeight: "1.4",
                                fontFamily:
                                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                "&:hover": {
                                  color: "#00509e",
                                  cursor: "pointer",
                                },
                                transition: "color 0.2s ease-in-out",
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              dangerouslySetInnerHTML={{
                                __html:
                                  formatStatuteTitleHtml(
                                    statute?.highlighted_statute_name,
                                  ) ||
                                  formatStatuteTitleHtml(
                                    statute?.highlighted_Statute_Name,
                                  ) ||
                                  formatStatuteTitle(statute.Statute_Name) ||
                                  "",
                              }}
                              onClick={() => {
                                handleStatuteNameClick(
                                  statute.Statute_Name || statute.Statute_name,
                                );
                              }}
                            />

                            {/* Action Buttons Section */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexShrink: 0,
                              }}
                            >
                              <Tooltip title="Sections" arrow placement="top">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    backgroundColor: "#FEF3C7",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      backgroundColor: "#FDE68A",
                                    },
                                  }}
                                  onClick={() => {
                                    handleSectionsClick(
                                      statute.Statute_name ||
                                        statute.Statute_Name,
                                      statute.doc_id || index,
                                    );
                                  }}
                                >
                                  {openSections[statute.doc_id] ? (
                                    <ChevronUp size={16} color="#D97706" />
                                  ) : (
                                    <ChevronDown size={16} color="#D97706" />
                                  )}
                                </Box>
                              </Tooltip>
                              <Tooltip title="Read" arrow placement="top">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    backgroundColor: "#DCFCE7",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      backgroundColor: "#BBF7D0",
                                    },
                                  }}
                                  onClick={() => {
                                    handleStatuteNameClick(
                                      statute.Statute_name ||
                                        statute.Statute_Name,
                                    );
                                  }}
                                >
                                  <BookOpen size={16} color="#16A34A" />
                                </Box>
                              </Tooltip>
                              <Tooltip title="Cases" arrow placement="top">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    backgroundColor: "#E0E7FF",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      backgroundColor: "#C7D2FE",
                                    },
                                  }}
                                  onClick={() => {
                                    handleRelevantCasesClick(
                                      statute?.Bookmark_ID,
                                    );
                                  }}
                                >
                                  <Scale size={16} color="#6366F1" />
                                </Box>
                              </Tooltip>
                              <Tooltip title="Copy Link" arrow placement="top">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    backgroundColor: "#F1F5F9",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      backgroundColor: "#E2E8F0",
                                    },
                                  }}
                                  onClick={() => {
                                    copyStatuteLink(
                                      statute.Statute_name ||
                                        statute.Statute_Name,
                                    );
                                  }}
                                >
                                  <Link2 size={16} color="#475569" />
                                </Box>
                              </Tooltip>
                              <Tooltip title="Bookmark" arrow placement="top">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    backgroundColor: "#FEE2E2",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      backgroundColor: "#FECACA",
                                    },
                                  }}
                                  onClick={() =>
                                    bookmarkedStatuteIds.has(
                                      statute?.Bookmark_ID,
                                    )
                                      ? handleUnBookmarkStatute(
                                          statute.Bookmark_ID,
                                        )
                                      : handleBookmarkClick(statute, "statute")
                                  }
                                >
                                  {bookmarkedStatuteIds.has(
                                    statute.Bookmark_ID,
                                  ) ? (
                                    <Bookmark
                                      size={16}
                                      color="#DC2626"
                                      fill="#DC2626"
                                    />
                                  ) : (
                                    <Bookmark size={16} color="#DC2626" />
                                  )}
                                </Box>
                              </Tooltip>
                              <Tooltip
                                title="Add as reference"
                                arrow
                                placement="top"
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    backgroundColor: "#FDF4FF",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      backgroundColor: "#FCE7F3",
                                    },
                                  }}
                                  onClick={() => {
                                    handleOpen();
                                    setReferenceStatute(statute);
                                  }}
                                >
                                  <StickyNote
                                    size={16}
                                    color='#C026D3'
                                    style={{ cursor: "pointer" }}
                                  />
                                </Box>
                              </Tooltip>
                              <Tooltip
                                title={
                                  isPdfDisabled ? "PDF Not Available" : "PDF"
                                }
                                arrow
                                placement='top'
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "8px",
                                    borderRadius: "6px",
                                    backgroundColor: isPdfDisabled
                                      ? "#F1F5F9"
                                      : "#CCFBF1",
                                    cursor: isPdfDisabled
                                      ? "not-allowed"
                                      : "pointer",
                                    opacity: isPdfDisabled ? 0.5 : 1,
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      backgroundColor: isPdfDisabled
                                        ? "#F1F5F9"
                                        : "#99F6E4",
                                    },
                                  }}
                                  onClick={() => {
                                    if (isPdfDisabled) return;
                                    handlePdfClick(
                                      statute?.Blob_Url,
                                      statute?.Bookmark_ID,
                                    );
                                  }}
                                >
                                  <FileText
                                    size={16}
                                    color={
                                      isPdfDisabled ? "#94A3B8" : "#0D9488"
                                    }
                                  />
                                </Box>
                              </Tooltip>
                              <DateChip date={statute?.Date} />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
            </List>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                padding: "10px 0",
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                border: "1px solid #F1F5F9",
                marginTop: "0px",
                marginBottom: "32px",
              }}
            >
              <Pagination
                count={Math.ceil(totalResults / resultsCount)}
                page={currentPage}
                onChange={handlePageChange}
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontSize: "14px",
                    minWidth: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    fontWeight: "500",
                    color: "#64748B",
                    border: "1px solid #E2E8F0",
                    backgroundColor: "#FFFFFF",
                    "&:hover": {
                      color: "#00509e",
                      backgroundColor: "#F0F9FF",
                      borderColor: "#00509e",
                    },
                    "&.Mui-selected": {
                      color: "#FFFFFF",
                      backgroundColor: "#00509e",
                      borderColor: "#00509e",
                      "&:hover": {
                        backgroundColor: "#003366",
                        borderColor: "#003366",
                      },
                    },
                  },
                }}
              />
              {snackbarMessage && (
                <Snackbar
                  open={!!snackbarMessage}
                  autoHideDuration={6000}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  onClose={() => setSnackbarMessage(null)}
                >
                  <Alert
                    onClose={() => setSnackbarMessage(null)}
                    severity={snackbarMessage.type}
                    sx={{ width: "100%" }}
                  >
                    {snackbarMessage.text}
                  </Alert>
                </Snackbar>
              )}
            </Box>
          </>
        )}
      </Box>
      <StatuteDetailDialog
        open={openSectionsDialog}
        onClose={handleCloseSectionsDialog}
      />
      <StatutePdfViewer
        open={openPdfPreview}
        onClose={() => setOpenPdfPreview(false)}
      />
      {openBookmarkModal && (
        <BookmarkDialog
          open={openBookmarkModal}
          onClose={handleCloseBookmarkModal}
          selectedStatute={selectedStatute?.statute}
          type={selectedStatute?.type}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setBookmarkedStatuteIds={setBookmarkedStatuteIds}
        />
      )}

      <InfoDialog
        open={openInfoDialog}
        handleClose={handleCloseInfoDialog}
        isStatuteSearch={isStatuteSearch}
      />
      {open && (
        <CMSProvider>
          <ReferenceCaseModal
            open={open}
            onClose={handleClose}
            type="statute"
            selectedReferenceCase={selectedReferenceStatute}
            setSelectedReferenceCase={setSelectedReferenceStatute}
            isAddCaseLoading={isAddStatuteLoading}
            handleAddCaseToCaseManagement={handleAddStatuteToCaseManagement}
          />
        </CMSProvider>
      )}
    </>
  );
};

export default StatuteSearch;
